import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Brain, Plus, Trash, PencilSimple, X, ArrowsClockwise, Check, Warning,
  Lightning, Shield, Scales, Eye, ListChecks, FloppyDisk, Lightbulb, Gear, 
  ChartBar, Target, TreeStructure, TextT, ToggleLeft, ToggleRight, Code,
  Database, Books, Files, CloudArrowDown
} from '@phosphor-icons/react';
import { API, useAuth } from '@/App';
import Sidebar from '@/components/Sidebar';

const NAVI_AVATAR = "https://customer-assets.emergentagent.com/job_risk-engine-pro-2/artifacts/0rppkc2h_image.png";

const RULE_CATEGORIES = [
  { id: 'compliance_priorities', label: 'Compliance Priorities', icon: Target, color: '#F59E0B', description: 'What NAVI should prioritize in assessments' },
  { id: 'decision_logic', label: 'Decision Logic', icon: TreeStructure, color: '#3B82F6', description: 'How NAVI should make compliance decisions' },
  { id: 'breach_thresholds', label: 'Breach Thresholds', icon: Warning, color: '#EF4444', description: 'When to trigger breach response mode' },
  { id: 'questioning_style', label: 'Questioning Style', icon: TextT, color: '#10B981', description: 'How NAVI should probe and challenge' },
  { id: 'industry_context', label: 'Industry Context', icon: Scales, color: '#8B5CF6', description: 'Industry-specific rules and context' },
  { id: 'response_format', label: 'Response Format', icon: Code, color: '#EC4899', description: 'How NAVI should structure responses' },
  { id: 'risk_assessment', label: 'Risk Assessment', icon: ChartBar, color: '#14B8A6', description: 'How NAVI should evaluate risk levels' },
  { id: 'custom', label: 'Custom Rules', icon: Gear, color: '#6B7280', description: 'Other custom logic and rules' }
];

const DEFAULT_RULES = {
  compliance_priorities: [
    { id: 'p1', title: 'FICA First', content: 'Always prioritize FICA/AML compliance concerns over other regulatory themes when multiple issues are detected.', priority: 'high', enabled: true },
    { id: 'p2', title: 'Data Breach Urgency', content: 'Data protection breaches involving personal information should be escalated immediately to breach response mode.', priority: 'high', enabled: true },
    { id: 'p3', title: 'Regulatory Deadlines', content: 'Flag any compliance issue that has a regulatory deadline within the next 30 days as high priority.', priority: 'medium', enabled: true }
  ],
  decision_logic: [
    { id: 'd1', title: 'Evidence-Based Assessment', content: 'Do not conclude compliance status without asking for supporting evidence. If a user claims they are compliant, ask for documentation.', priority: 'high', enabled: true },
    { id: 'd2', title: 'Challenge Assumptions', content: 'When users make broad compliance statements, probe deeper with specific questions about controls, processes, and documentation.', priority: 'high', enabled: true },
    { id: 'd3', title: 'Risk-Based Thinking', content: 'Always consider inherent risk vs residual risk. Ask about controls in place and their effectiveness.', priority: 'medium', enabled: true }
  ],
  breach_thresholds: [
    { id: 'b1', title: 'Immediate Breach Trigger', content: 'Activate breach response mode immediately when detecting: data leak, unauthorized access, regulatory investigation, fraud, sanction violation.', priority: 'critical', enabled: true },
    { id: 'b2', title: 'Pattern Detection', content: 'If 3 or more compliance concerns are mentioned in a single conversation, automatically escalate severity assessment.', priority: 'high', enabled: true }
  ],
  questioning_style: [
    { id: 'q1', title: 'Professional but Firm', content: 'Maintain a professional tone while being firm on compliance requirements. Do not accept vague answers.', priority: 'high', enabled: true },
    { id: 'q2', title: 'Regulatory Context', content: 'Always cite the specific regulation or requirement when asking probing questions. Example: "Under FICA Section 21..."', priority: 'medium', enabled: true }
  ],
  industry_context: [
    { id: 'i1', title: 'South African Focus', content: 'All advice should be contextualized for South African regulatory environment. Reference local regulators (FSCA, PA, FIC, SARB).', priority: 'high', enabled: true },
    { id: 'i2', title: 'Accountable Institution Awareness', content: 'Tailor questions and assessments based on the type of accountable institution the client is classified as under FICA.', priority: 'high', enabled: true }
  ],
  response_format: [
    { id: 'r1', title: 'Structured Responses', content: 'Use clear headings, bullet points, and numbered lists for complex responses. Never provide wall-of-text answers.', priority: 'medium', enabled: true },
    { id: 'r2', title: 'Action-Oriented', content: 'Always end responses with clear next steps or actions the user should take.', priority: 'medium', enabled: true }
  ],
  risk_assessment: [
    { id: 'ra1', title: 'Risk Rating Framework', content: 'Use the standard risk matrix: Likelihood (1-5) x Impact (1-5) = Risk Score. Scores 15+ are Critical, 10-14 High, 5-9 Medium, 1-4 Low.', priority: 'high', enabled: true }
  ],
  custom: []
};

const NaviLogicPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('compliance_priorities');
  const [rules, setRules] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [knowledgeStats, setKnowledgeStats] = useState(null);
  const [refreshingKnowledge, setRefreshingKnowledge] = useState(false);
  const [fscaStatus, setFscaStatus] = useState(null);
  const [syncingFsca, setSyncingFsca] = useState(false);

  const isSuperAdmin = user?.is_super_admin;
  const currentCategory = RULE_CATEGORIES.find(c => c.id === selectedCategory) || RULE_CATEGORIES[0];
  const CategoryIcon = currentCategory.icon;

  useEffect(() => {
    if (isSuperAdmin) {
      loadRules();
      loadKnowledgeStats();
      loadFscaStatus();
    }
  }, [isSuperAdmin]);

  const loadKnowledgeStats = async () => {
    try {
      const response = await axios.get(`${API}/navi/knowledge-base/stats`);
      setKnowledgeStats(response.data);
    } catch {
      // Silent fail - stats are optional
    }
  };

  const refreshKnowledgeBase = async () => {
    setRefreshingKnowledge(true);
    try {
      const response = await axios.post(`${API}/navi/refresh-knowledge-base`);
      toast.success(response.data.message);
      loadKnowledgeStats();
    } catch {
      toast.error('Failed to refresh knowledge base');
    } finally {
      setRefreshingKnowledge(false);
    }
  };

  const loadFscaStatus = async () => {
    try {
      const response = await axios.get(`${API}/fsca/status`);
      setFscaStatus(response.data.status);
    } catch {
      // Silent fail - FSCA status is optional
    }
  };

  const syncFscaDocuments = async () => {
    setSyncingFsca(true);
    try {
      const response = await axios.post(`${API}/fsca/sync`);
      toast.success(response.data.message);
      loadFscaStatus();
      loadKnowledgeStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync FSCA documents');
    } finally {
      setSyncingFsca(false);
    }
  };

  const loadRules = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/navi/logic-rules`);
      setRules(response.data.rules && Object.keys(response.data.rules).length > 0 ? response.data.rules : DEFAULT_RULES);
    } catch {
      setRules(DEFAULT_RULES);
    } finally {
      setLoading(false);
    }
  };

  const saveRules = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/navi/logic-rules`, { rules });
      toast.success('NAVI Logic rules saved successfully');
    } catch {
      toast.error('Failed to save rules');
    } finally {
      setSaving(false);
    }
  };

  const toggleRule = (categoryId, ruleId) => {
    setRules(prev => ({
      ...prev,
      [categoryId]: prev[categoryId].map(rule =>
        rule.id === ruleId ? { ...rule, enabled: !rule.enabled } : rule
      )
    }));
  };

  const deleteRule = (categoryId, ruleId) => {
    if (!confirm('Delete this rule?')) return;
    setRules(prev => ({
      ...prev,
      [categoryId]: prev[categoryId].filter(rule => rule.id !== ruleId)
    }));
  };

  const addRule = (newRule) => {
    setRules(prev => ({
      ...prev,
      [selectedCategory]: [...(prev[selectedCategory] || []), { ...newRule, id: `${selectedCategory}_${Date.now()}` }]
    }));
    setShowAddModal(false);
  };

  const updateRule = (updatedRule) => {
    setRules(prev => ({
      ...prev,
      [selectedCategory]: prev[selectedCategory].map(rule =>
        rule.id === updatedRule.id ? updatedRule : rule
      )
    }));
    setEditingRule(null);
  };

  const getEnabledCount = (categoryId) => (rules[categoryId] || []).filter(r => r.enabled).length;
  const getTotalCount = (categoryId) => (rules[categoryId] || []).length;

  if (!isSuperAdmin) {
    return (
      <div className="min-h-screen bg-background flex">
        <Sidebar />
        <div className="ml-64 flex-1 flex items-center justify-center">
          <div className="text-center">
            <Brain size={64} className="mx-auto text-text-tertiary mb-4" />
            <h1 className="text-xl font-heading text-text-primary mb-2">Access Denied</h1>
            <p className="text-text-secondary">Super admin access required.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex">
      <Sidebar />
      <div className="ml-64 flex-1 flex">
        {/* Sidebar */}
        <div className="w-72 border-r border-border bg-background-surface p-4 min-h-screen">
          <div className="flex items-center gap-3 mb-6 p-3 bg-gradient-to-r from-amber-500/10 to-purple-500/10 border border-amber-500/20 rounded-sm">
            <img src={NAVI_AVATAR} alt="NAVI" className="w-12 h-12 rounded-full border-2 border-amber-500 object-cover" />
            <div>
              <h3 className="font-heading font-medium text-text-primary">NAVI Logic</h3>
              <p className="text-xs text-text-tertiary">AI Decision Rules</p>
            </div>
          </div>
          
          <h3 className="text-xs uppercase tracking-wider text-text-tertiary font-semibold px-3 mb-4">Rule Categories</h3>
          
          <div className="space-y-1">
            {RULE_CATEGORIES.map((category) => {
              const Icon = category.icon;
              const isActive = selectedCategory === category.id;
              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  data-testid={`category-${category.id}`}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-sm transition-all ${isActive ? 'bg-gold/10 border-l-2' : 'hover:bg-background-hover'}`}
                  style={{ borderColor: isActive ? category.color : 'transparent' }}
                >
                  <Icon size={20} weight={isActive ? 'fill' : 'regular'} style={{ color: isActive ? category.color : 'var(--text-secondary)' }} />
                  <div className="flex-1 text-left">
                    <span className={`text-sm font-medium block ${isActive ? 'text-text-primary' : 'text-text-secondary'}`}>{category.label}</span>
                    <span className="text-xs text-text-tertiary">{getEnabledCount(category.id)}/{getTotalCount(category.id)} active</span>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-6 pt-4 border-t border-border space-y-2">
            <button onClick={saveRules} disabled={saving} className="w-full flex items-center justify-center gap-2 px-3 py-2.5 bg-gold hover:bg-gold-hover text-gold-foreground rounded-sm transition-all disabled:opacity-50">
              {saving ? <ArrowsClockwise size={16} className="animate-spin" /> : <FloppyDisk size={16} />}
              Save All Rules
            </button>
            <button onClick={() => { if(confirm('Reset to defaults?')) setRules(DEFAULT_RULES); }} className="w-full flex items-center justify-center gap-2 px-3 py-2.5 bg-background border border-border text-text-secondary hover:border-gold hover:text-gold rounded-sm transition-all">
              <ArrowsClockwise size={16} />
              Reset to Defaults
            </button>
          </div>

          {/* Knowledge Base Stats */}
          <div className="mt-6 pt-4 border-t border-border">
            <div className="px-3 mb-3 flex items-center justify-between">
              <h4 className="text-xs uppercase tracking-wider text-text-tertiary font-semibold">Knowledge Base</h4>
              <button 
                onClick={refreshKnowledgeBase}
                disabled={refreshingKnowledge}
                className="p-1 text-text-tertiary hover:text-gold transition-colors"
                title="Refresh Knowledge Base"
              >
                <CloudArrowDown size={16} className={refreshingKnowledge ? 'animate-spin' : ''} />
              </button>
            </div>
            {knowledgeStats ? (
              <div className="space-y-2 px-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-tertiary flex items-center gap-1"><Books size={14} /> Master Library</span>
                  <span className="font-mono text-gold">{knowledgeStats.master_library?.total_documents || 0}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-tertiary flex items-center gap-1"><Database size={14} /> Knowledge Index</span>
                  <span className="font-mono text-blue-400">{knowledgeStats.knowledge_base?.total_entries || 0}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-tertiary flex items-center gap-1"><ListChecks size={14} /> Active Rules</span>
                  <span className="font-mono text-green-400">{knowledgeStats.logic_rules?.active_rules || 0}</span>
                </div>
              </div>
            ) : (
              <p className="text-xs text-text-tertiary px-3">Loading stats...</p>
            )}
          </div>

          {/* FSCA Document Sync */}
          <div className="mt-6 pt-4 border-t border-border">
            <div className="px-3 mb-3 flex items-center justify-between">
              <h4 className="text-xs uppercase tracking-wider text-text-tertiary font-semibold">FSCA Sync</h4>
              <button 
                onClick={syncFscaDocuments}
                disabled={syncingFsca}
                className="p-1 text-text-tertiary hover:text-gold transition-colors"
                title="Sync FSCA Documents"
              >
                <CloudArrowDown size={16} className={syncingFsca ? 'animate-spin' : ''} />
              </button>
            </div>
            <div className="space-y-2 px-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-text-tertiary flex items-center gap-1"><Files size={14} /> FSCA Docs</span>
                <span className="font-mono text-amber-400">{fscaStatus?.total_synced || 0}</span>
              </div>
              {fscaStatus?.last_sync && (
                <p className="text-xs text-text-tertiary">
                  Last sync: {new Date(fscaStatus.last_sync).toLocaleDateString()}
                </p>
              )}
              <a 
                href="https://www.fsca.co.za/Supervisory-Information/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-xs text-gold hover:underline block"
              >
                View FSCA Portal →
              </a>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 border-b border-border" style={{ background: `linear-gradient(135deg, ${currentCategory.color}15 0%, ${currentCategory.color}05 100%)`, borderLeft: `4px solid ${currentCategory.color}` }}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${currentCategory.color}20` }}>
                  <CategoryIcon size={28} style={{ color: currentCategory.color }} />
                </div>
                <div>
                  <h1 className="text-2xl font-heading font-medium text-text-primary">{currentCategory.label}</h1>
                  <p className="text-sm text-text-secondary mt-1">{currentCategory.description}</p>
                </div>
              </div>
              <button onClick={() => setShowAddModal(true)} className="px-4 py-2 bg-gold hover:bg-gold-hover text-gold-foreground rounded-sm flex items-center gap-2 text-sm transition-all">
                <Plus size={16} />Add Rule
              </button>
            </div>
          </div>

          <div className="p-6">
            {loading ? (
              <div className="flex items-center justify-center py-16"><ArrowsClockwise size={32} className="animate-spin text-gold" /></div>
            ) : (
              <div className="space-y-4">
                <div className="bg-background-surface border border-amber-500/30 rounded-sm p-4 flex items-start gap-3">
                  <Lightbulb size={24} className="text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-text-primary mb-1">How NAVI Uses These Rules</h4>
                    <p className="text-sm text-text-secondary">NAVI reads enabled rules before responding. Rules are applied contextually based on conversation topic.</p>
                  </div>
                </div>

                {(rules[selectedCategory] || []).length > 0 ? (
                  <div className="space-y-3">
                    {(rules[selectedCategory] || []).map((rule) => (
                      <RuleCard key={rule.id} rule={rule} categoryColor={currentCategory.color} onToggle={() => toggleRule(selectedCategory, rule.id)} onEdit={() => setEditingRule(rule)} onDelete={() => deleteRule(selectedCategory, rule.id)} />
                    ))}
                  </div>
                ) : (
                  <div className="bg-background-surface border border-border rounded-sm p-8 text-center">
                    <ListChecks size={48} className="mx-auto text-text-tertiary mb-4" />
                    <p className="text-text-secondary mb-4">No rules in this category</p>
                    <button onClick={() => setShowAddModal(true)} className="px-4 py-2 bg-gold hover:bg-gold-hover text-gold-foreground rounded-sm transition-all">Add First Rule</button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <RuleModal isOpen={showAddModal || editingRule !== null} onClose={() => { setShowAddModal(false); setEditingRule(null); }} rule={editingRule} category={currentCategory} onSave={editingRule ? updateRule : addRule} />
    </div>
  );
};

const RuleCard = ({ rule, categoryColor, onToggle, onEdit, onDelete }) => {
  const priorityColors = { critical: 'bg-red-500/10 text-red-400 border-red-500/30', high: 'bg-amber-500/10 text-amber-400 border-amber-500/30', medium: 'bg-blue-500/10 text-blue-400 border-blue-500/30', low: 'bg-green-500/10 text-green-400 border-green-500/30' };
  return (
    <div className={`bg-background-surface border rounded-sm p-4 transition-all ${rule.enabled ? 'border-border' : 'border-border/50 opacity-60'}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <button onClick={onToggle}>
            {rule.enabled ? <ToggleRight size={28} weight="fill" style={{ color: categoryColor }} /> : <ToggleLeft size={28} className="text-text-tertiary" />}
          </button>
          <div>
            <h4 className="font-medium text-text-primary">{rule.title}</h4>
            <span className={`inline-block px-2 py-0.5 text-xs rounded border mt-1 ${priorityColors[rule.priority]}`}>{rule.priority.toUpperCase()}</span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onEdit} className="p-1.5 text-text-tertiary hover:text-gold transition-colors"><PencilSimple size={16} /></button>
          <button onClick={onDelete} className="p-1.5 text-text-tertiary hover:text-risk-high-text transition-colors"><Trash size={16} /></button>
        </div>
      </div>
      <p className="text-sm text-text-secondary pl-10">{rule.content}</p>
    </div>
  );
};

const RuleModal = ({ isOpen, onClose, rule, category, onSave }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [priority, setPriority] = useState('medium');
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    if (rule) { setTitle(rule.title); setContent(rule.content); setPriority(rule.priority); setEnabled(rule.enabled); }
    else { setTitle(''); setContent(''); setPriority('medium'); setEnabled(true); }
  }, [rule, isOpen]);

  const handleSave = () => {
    if (!title.trim() || !content.trim()) { toast.error('Title and content required'); return; }
    onSave({ ...(rule || {}), title: title.trim(), content: content.trim(), priority, enabled });
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={onClose}>
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} onClick={(e) => e.stopPropagation()} className="bg-background-surface border border-border rounded-sm w-full max-w-lg">
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div>
              <h2 className="text-lg font-heading font-medium text-text-primary">{rule ? 'Edit Rule' : 'Add New Rule'}</h2>
              <p className="text-sm text-text-secondary mt-1">{category.label}</p>
            </div>
            <button onClick={onClose} className="text-text-tertiary hover:text-text-primary"><X size={20} /></button>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm text-text-secondary mb-2">Rule Title *</label>
              <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g., Evidence-Based Assessment" className="w-full px-3 py-2.5 bg-background border border-border rounded-sm text-text-primary placeholder:text-text-tertiary focus:border-gold focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm text-text-secondary mb-2">Rule Content *</label>
              <textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder="Describe what NAVI should do..." rows={4} className="w-full px-3 py-2.5 bg-background border border-border rounded-sm text-text-primary placeholder:text-text-tertiary focus:border-gold focus:outline-none resize-none" />
            </div>
            <div>
              <label className="block text-sm text-text-secondary mb-2">Priority</label>
              <div className="flex gap-2">
                {['critical', 'high', 'medium', 'low'].map((p) => (
                  <button key={p} onClick={() => setPriority(p)} className={`px-3 py-1.5 text-sm rounded-sm border transition-all ${priority === p ? (p === 'critical' ? 'bg-red-500/20 text-red-400 border-red-500' : p === 'high' ? 'bg-amber-500/20 text-amber-400 border-amber-500' : p === 'medium' ? 'bg-blue-500/20 text-blue-400 border-blue-500' : 'bg-green-500/20 text-green-400 border-green-500') : 'bg-background border-border text-text-secondary hover:border-gold'}`}>
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div><label className="block text-sm text-text-primary">Enabled</label><span className="text-xs text-text-tertiary">Rule active when enabled</span></div>
              <button onClick={() => setEnabled(!enabled)}>{enabled ? <ToggleRight size={32} weight="fill" className="text-gold" /> : <ToggleLeft size={32} className="text-text-tertiary" />}</button>
            </div>
          </div>
          <div className="p-6 border-t border-border flex justify-end gap-3">
            <button onClick={onClose} className="px-4 py-2 bg-background border border-border text-text-primary rounded-sm hover:border-gold transition-all">Cancel</button>
            <button onClick={handleSave} className="px-4 py-2 bg-gold hover:bg-gold-hover text-gold-foreground rounded-sm transition-all flex items-center gap-2"><Check size={16} />{rule ? 'Update' : 'Add'}</button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default NaviLogicPage;
